import { Fragment, useState, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { formatHours, formatSeconds } from "@/lib/format";
import { useTimer } from "@/contexts/TimerContext";
import { useTimeEntries, type TimeEntry } from "@/hooks/use-time-registration";
import type { InternalProject, MyTask } from "@/hooks/use-my-tasks";
import { useInternalProjectTasks, type InternalProjectTask } from "@/hooks/use-internal-project-tasks";
import TimeTrackingPopup from "./TimeTrackingPopup";
import { SrIcon } from "@/components/ui/SrIcon";
import { usePersistedCollapse } from "@/hooks/use-persisted-collapse";
import { InternalProjectTaskRow, InternalProjectTaskMobileCard } from "./InternalProjectTaskRow";
import { CreateInternalTaskDialog } from "./CreateInternalTaskDialog";

/** Shape an internal project into a MyTask-compatible object for TimeTrackingPopup */
function toTask(p: InternalProject): MyTask {
  return {
    id: p.id,
    budget_line_id: "",
    project_id: p.id,
    task_description: p.name,
    task_status: "in_progress",
    phase: "",
    project_name: p.name,
    assigned_hours: 0,
    logged_hours: 0,
  };
}

function useProjectRowState(project: InternalProject, entries: TimeEntry[]) {
  const { activeTimer, startTimer, stopTimer, getElapsed } = useTimer();

  const loggedSeconds = entries
    .filter((e) => e.project_id === project.id)
    .reduce((sum, e) => sum + (e.duration_seconds ?? 0), 0);
  const loggedHours = loggedSeconds / 3600;

  const isTaskActive = activeTimer?.taskId === project.id;
  const timerDisplay = isTaskActive
    ? formatSeconds(getElapsed())
    : formatHours(loggedHours);

  const handleTimerToggle = () => {
    if (isTaskActive) {
      stopTimer();
    } else {
      startTimer({
        taskId: project.id,
        projectId: project.id,
        projectName: project.name,
        taskDescription: project.name,
        phase: "",
        budgetLineId: null,
      });
    }
  };

  return { isTaskActive, timerDisplay, handleTimerToggle };
}

function ProjectMobileCard({
  project,
  userId,
  entries,
}: {
  project: InternalProject;
  userId: string | null;
  entries: TimeEntry[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [showTimePopup, setShowTimePopup] = useState(false);
  const timeAnchorRef = useRef<HTMLButtonElement>(null);

  const loggedSeconds = entries
    .filter((e) => e.project_id === project.id)
    .reduce((sum, e) => sum + (e.duration_seconds ?? 0), 0);
  const timerDisplay = formatHours(loggedSeconds / 3600);

  return (
    <div className="border-b border-border/50 last:border-0">
      <button
        className="w-full flex items-center gap-3 px-4 py-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{project.name}</p>
          <p className="text-xs text-muted-foreground">Internt prosjekt</p>
        </div>
        <p className="font-mono text-sm tabular-nums flex-shrink-0">
          {timerDisplay}
        </p>
        {expanded ? (
          <SrIcon name="chevron-up" className="w-4 h-4 text-muted-foreground flex-shrink-0"  />
        ) : (
          <SrIcon name="chevron-down" className="w-4 h-4 text-muted-foreground flex-shrink-0"  />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border/30 pt-3">
          <button
            ref={timeAnchorRef}
            onClick={() => setShowTimePopup(!showTimePopup)}
            className="w-full h-12 flex items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
          >
            Logg tid
          </button>
          {showTimePopup && (
            <TimeTrackingPopup
              task={toTask(project)}
              onClose={() => setShowTimePopup(false)}
              userId={userId}
              anchorRef={timeAnchorRef}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ProjectRow({
  project,
  userId,
  entries,
}: {
  project: InternalProject;
  userId: string | null;
  entries: TimeEntry[];
}) {
  const [showTimePopup, setShowTimePopup] = useState(false);
  const timeAnchorRef = useRef<HTMLButtonElement>(null);
  const { isTaskActive, timerDisplay, handleTimerToggle } = useProjectRowState(project, entries);

  return (
    <tr className="border-b border-border/30 last:border-0 hover:bg-muted/20">
      <td className="py-2.5 px-4">
        <span className="text-sm font-medium">{project.name}</span>
      </td>
      <td className="py-2.5 px-4">
        <span className="text-xs text-muted-foreground">Internt prosjekt</span>
      </td>
      <td className="py-2.5 px-4 text-right tabular-nums text-sm text-muted-foreground">
        0t
      </td>
      <td className="py-2.5 px-4">
        {/* Fast-bredde, venstrejustert wrapper sentrert i cellen → start-knappen
            havner alltid på samme x uavhengig av tid-tekstens bredde. */}
        <div className="flex justify-center">
          <div className="flex items-center gap-2 w-[112px]">
            <button
              onClick={handleTimerToggle}
              aria-label={isTaskActive ? "Pause" : "Start"}
              className={cn(
                "w-8 h-8 shrink-0 flex items-center justify-center rounded-full border-[1.5px] transition-colors",
                isTaskActive
                  ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                  : "bg-card text-foreground border-foreground hover:bg-muted",
              )}
            >
              <SrIcon name={isTaskActive ? "pause" : "play"} className="w-3.5 h-3.5" />
            </button>
            <button
              ref={timeAnchorRef}
              onClick={() => setShowTimePopup(!showTimePopup)}
              className={cn(
                "font-mono text-sm cursor-pointer hover:underline text-left py-1 px-1 shrink-0 whitespace-nowrap",
                isTaskActive && "text-blue-600 font-semibold"
              )}
            >
              {timerDisplay}
            </button>
          </div>
          {showTimePopup && (
            <TimeTrackingPopup
              task={toTask(project)}
              onClose={() => setShowTimePopup(false)}
              userId={userId}
              anchorRef={timeAnchorRef}
            />
          )}
        </div>
      </td>
      <td className="py-2.5 px-4 text-center">
        <span className="text-xs text-muted-foreground">—</span>
      </td>
    </tr>
  );
}

/** VIB-1208: rad/knapp for å opprette en ny oppgave på et gitt internprosjekt. */
function NewTaskRow({ project, colSpan }: { project: InternalProject; colSpan: number }) {
  const [open, setOpen] = useState(false);
  return (
    <tr className="border-b border-border/30 last:border-0">
      <td colSpan={colSpan} className="py-1.5 px-4 pl-8">
        <button onClick={() => setOpen(true)} className="text-xs text-primary hover:underline flex items-center gap-1">
          <SrIcon name="plus" className="w-3 h-3" />
          Ny oppgave på {project.name}
        </button>
        <CreateInternalTaskDialog open={open} onOpenChange={setOpen} projectId={project.id} projectName={project.name} />
      </td>
    </tr>
  );
}

function NewTaskButtonMobile({ project }: { project: InternalProject }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pl-4 pb-2">
      <button onClick={() => setOpen(true)} className="text-xs text-primary hover:underline flex items-center gap-1 px-4 py-1.5">
        <SrIcon name="plus" className="w-3 h-3" />
        Ny oppgave på {project.name}
      </button>
      <CreateInternalTaskDialog open={open} onOpenChange={setOpen} projectId={project.id} projectName={project.name} />
    </div>
  );
}

export default function InternalProjectsSection({
  projects,
  userId,
}: {
  projects: InternalProject[];
  userId: string | null;
}) {
  // VIB-2100: husket per bruker. Default er fortsatt lukket.
  const [expanded, setExpanded] = usePersistedCollapse("internal-projects", false);
  const { data: entries = [] } = useTimeEntries(userId, "2000-01-01", "2099-12-31");

  // VIB-1208: hvert internprosjekt har sin egen rad (timer rett på
  // prosjektet, som før), med oppgavene og «Ny oppgave» under.
  const { data: internalTasksData } = useInternalProjectTasks();
  const tasksByProject = useMemo(() => {
    const map = new Map<string, InternalProjectTask[]>();
    for (const t of internalTasksData ?? []) {
      const list = map.get(t.project_id) ?? [];
      list.push(t);
      map.set(t.project_id, list);
    }
    return map;
  }, [internalTasksData]);

  if (projects.length === 0) return null;

  return (
    <div className="border-[1.5px] border-foreground bg-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-display text-lg tracking-wide">Interne prosjekter</span>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
            {projects.length}
          </span>
        </div>
        {expanded ? (
          <SrIcon name="chevron-up" className="w-4 h-4 text-muted-foreground"  />
        ) : (
          <SrIcon name="chevron-down" className="w-4 h-4 text-muted-foreground"  />
        )}
      </button>

      {expanded && (
        <div className="border-t">
          {/* Desktop: table */}
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground text-xs uppercase tracking-wider border-b">
                  <th className="text-left py-2 px-4 font-medium">Prosjekt</th>
                  <th className="text-left py-2 px-4 font-medium">Oppgave</th>
                  <th className="text-right py-2 px-4 font-medium">Timer</th>
                  <th className="text-center py-2 px-4 font-medium">Tidssporing</th>
                  <th className="text-center py-2 px-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => {
                  const tasks = tasksByProject.get(p.id) ?? [];
                  return (
                    <Fragment key={p.id}>
                      <ProjectRow project={p} userId={userId} entries={entries} />
                      {tasks.map((t) => (
                        <InternalProjectTaskRow key={t.id} task={t} userId={userId} entries={entries} />
                      ))}
                      <NewTaskRow project={p} colSpan={5} />
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list */}
          <div className="md:hidden">
            {projects.map((p) => {
              const tasks = tasksByProject.get(p.id) ?? [];
              return (
                <div key={p.id} className="border-b border-border/50 last:border-0">
                  <ProjectMobileCard project={p} userId={userId} entries={entries} />
                  {tasks.map((t) => (
                    <InternalProjectTaskMobileCard key={t.id} task={t} userId={userId} entries={entries} />
                  ))}
                  <NewTaskButtonMobile project={p} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
